/**
 * WASLIO — interaction layer
 *
 * Native only (no libraries): IntersectionObserver reveals, Web Animations hero entrance,
 * requestAnimationFrame pointer spotlight, accessible accordions, seamless marquee,
 * search shortcut. Commerce behaviour (cart, search results, filters, variants, wishlist)
 * stays in Minimog — nothing here replaces it.
 *
 * Every initialiser is idempotent and scoped, so Theme Editor section reloads are safe.
 */
(function () {
  'use strict';

  var ML = (window.Waslio = window.Waslio || {});
  var cfg = ML.settings || {};
  var doc = document;
  var root = doc.documentElement;

  var reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  function motionAllowed() {
    return cfg.motion !== false && !reduceMotionQuery.matches;
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  /**
   * Runs fn once per element. The registry lives in a WeakMap, NOT in a data attribute: an attribute
   * is part of the markup, so anything that clones or re-serialises a section (innerHTML copies,
   * some apps, editor tooling) would carry "already initialised" onto a fresh element that has no
   * listeners, leaving it dead. A WeakMap is tied to the actual node and is garbage-collected with it.
   */
  var initRegistry = typeof WeakMap === 'function' ? new WeakMap() : null;

  function once(el, key, fn) {
    if (!el) return;
    if (!initRegistry) {
      var prop = '__mlInit' + key;
      if (el[prop]) return;
      el[prop] = true;
      fn(el);
      return;
    }
    var keys = initRegistry.get(el);
    if (!keys) {
      keys = {};
      initRegistry.set(el, keys);
    }
    if (keys[key]) return;
    keys[key] = true;
    fn(el);
  }

  /* ---------------------------------------------------------------- reveal */
  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );
    return revealObserver;
  }

  function initReveal(scope) {
    var items = scope.querySelectorAll('[data-ml-reveal]');
    if (!items.length) return;
    // The observer itself is created inline in <head> (snippets/ml-design-tokens.liquid) so that it
    // never waits on a deferred script. This only covers the cases where that engine is absent
    // (motion off, reduced motion, Theme Editor, very old browser): show everything straight away.
    var watch = motionAllowed() && !cfg.designMode ? ML.revealWatch : null;
    each(items, function (el) {
      if (watch) watch(el);
      else el.classList.add('is-in');
    });
  }

  function initHero(scope) {
    each(scope.querySelectorAll('[data-ml-hero-item]'), function (el, index) {
      once(el, 'Hero', function () {
        var animated = el.closest('[data-ml-animate]');
        el.classList.add('is-in');
        if (!motionAllowed() || !animated || cfg.designMode || typeof el.animate !== 'function') return;
        el.animate(
          [
            { opacity: 0, transform: 'translateY(18px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ],
          { duration: 720, delay: 60 + index * 90, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'backwards' }
        );
      });
    });
  }

  /* ------------------------------------------------------------- spotlight */
  // One delegated listener for the whole document: works for content injected later
  // (recommendations, filtered grids, editor reloads) without per-card handlers.
  var spotlightBound = false;

  function initSpotlight() {
    if (spotlightBound || cfg.spotlight === false) return;
    spotlightBound = true;

    var frame = 0;
    var target = null;
    var point = { x: 0, y: 0 };

    function paint() {
      frame = 0;
      if (!target || !target.isConnected) return;
      var rect = target.getBoundingClientRect();
      target.style.setProperty('--mx', point.x - rect.left + 'px');
      target.style.setProperty('--my', point.y - rect.top + 'px');
    }

    doc.addEventListener(
      'pointermove',
      function (event) {
        if (!finePointerQuery.matches || event.pointerType === 'touch') return;
        var el = event.target && event.target.closest ? event.target.closest('[data-ml-spotlight], .ml-spot') : null;
        if (!el) {
          target = null;
          return;
        }
        target = el;
        point.x = event.clientX;
        point.y = event.clientY;
        if (!frame) frame = window.requestAnimationFrame(paint);
      },
      { passive: true }
    );
  }

  /* ---------------------------------------------------- spotlight: plain cards
   * Cards that have no spotlight layer in their markup get the effect from CSS (.ml-spot::after)
   * once they carry the class. Pointer devices only; the class is harmless elsewhere. */
  var SPOT_SELECTOR = [
    '.ml-testimonial', '.ml-review', '.ml-trust--cards .ml-trust__item', '.ml-contact__row', '.ml-faq .ml-accordion__item',
    '.ml-tile', '.ml-deal-grid .ml-deal', '.ml-quick-grid .ml-shortcut', '.ml-promo', '.ml-nav-tile', '.ml-noresults__links a',
    '.ml-category-card:not([data-ml-spotlight])', '.ml-brand:not([data-ml-spotlight])', '.ml-assurance', '.ml-hero__showcase'
  ].join(',');

  function initSpot(scope) {
    if (!finePointerQuery.matches || cfg.spotlight === false) return;
    each((scope || doc).querySelectorAll(SPOT_SELECTOR), function (el) {
      if (el.querySelector('.ml-spotlight-layer')) return;
      el.classList.add('ml-spot');
    });
  }

  /* ---------------------------------------------------------------- ripple */
  var RIPPLE_SELECTOR = '.ml-btn, .sf__btn, .add-to-cart, .shopify-payment-button__button, .ml-tile, .ml-shortcut, .ml-category-card, .sf__btn-icon';
  var rippleBound = false;
  function initRipple() {
    if (rippleBound) return;
    rippleBound = true;
    doc.addEventListener(
      'pointerdown',
      function (event) {
        if (!motionAllowed() || event.button > 0) return;
        var el = event.target && event.target.closest ? event.target.closest(RIPPLE_SELECTOR) : null;
        if (!el || el.disabled) return;
        var rect = el.getBoundingClientRect();
        var size = Math.max(rect.width, rect.height) * 2.2;
        var dot = doc.createElement('span');
        dot.className = 'ml-ripple';
        dot.style.width = dot.style.height = size + 'px';
        dot.style.left = event.clientX - rect.left + 'px';
        dot.style.top = event.clientY - rect.top + 'px';
        var cs = window.getComputedStyle(el);
        if (cs.position === 'static') el.style.position = 'relative';
        if (cs.overflow === 'visible') el.style.overflow = 'hidden';
        el.appendChild(dot);
        dot.addEventListener('animationend', function () { dot.remove(); });
        window.setTimeout(function () { if (dot.parentNode) dot.remove(); }, 800);
      },
      { passive: true }
    );
  }

  /* --------------------------------------------------------- stepper bump */
  var bumpBound = false;
  function initStepperBump() {
    if (bumpBound) return;
    bumpBound = true;
    doc.addEventListener('click', function (event) {
      var btn = event.target && event.target.closest ? event.target.closest('.quantity-input button') : null;
      if (!btn || !motionAllowed()) return;
      var box = btn.closest('.quantity-input');
      if (!box) return;
      box.classList.remove('is-bumped');
      void box.offsetWidth;
      box.classList.add('is-bumped');
      window.setTimeout(function () { box.classList.remove('is-bumped'); }, 180);
    });
  }

  /* ------------------------------------------------------------- accordion */
  function setAccordionItem(item, open, animate) {
    var button = item.querySelector('.ml-accordion__btn');
    var panel = item.querySelector('.ml-accordion__panel');
    if (!button || !panel) return;

    button.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (open) {
      panel.hidden = false;
      // Force a style flush so the grid-rows transition runs from 0fr.
      if (animate) void panel.offsetHeight;
      item.classList.add('is-open');
      return;
    }

    item.classList.remove('is-open');
    if (!animate || !motionAllowed()) {
      panel.hidden = true;
      return;
    }
    var done = function (event) {
      if (event && event.target !== panel) return;
      panel.removeEventListener('transitionend', done);
      if (!item.classList.contains('is-open')) panel.hidden = true;
    };
    panel.addEventListener('transitionend', done);
    window.setTimeout(done, 400);
  }

  function initAccordions(scope) {
    each(scope.querySelectorAll('[data-ml-accordion]'), function (accordion) {
      once(accordion, 'Accordion', function () {
        accordion.addEventListener('click', function (event) {
          var button = event.target.closest('.ml-accordion__btn');
          if (!button || !accordion.contains(button)) return;
          var item = button.closest('.ml-accordion__item');
          var willOpen = !item.classList.contains('is-open');

          if (willOpen && accordion.hasAttribute('data-ml-accordion-single')) {
            each(accordion.querySelectorAll('.ml-accordion__item.is-open'), function (other) {
              if (other !== item) setAccordionItem(other, false, true);
            });
          }
          setAccordionItem(item, willOpen, true);
        });
      });
    });
  }

  /* --------------------------------------------------------------- marquee */
  function fitMarquee(marquee) {
    var track = marquee.querySelector('[data-ml-marquee-track]');
    var groups = marquee.querySelectorAll('[data-ml-marquee-group]');
    if (!track || groups.length < 2) return;

    var group = groups[0];
    var mirror = groups[1];
    var baseHTML = group.dataset.mlBase || (group.dataset.mlBase = group.innerHTML);
    var baseRepeats = 3;
    var width = group.dataset.mlBaseWidth ? parseFloat(group.dataset.mlBaseWidth) : group.scrollWidth;
    if (!width) return;
    group.dataset.mlBaseWidth = width;

    // Each half must be at least as wide as the viewport, or the loop shows a gap.
    var copies = Math.max(1, Math.ceil((marquee.clientWidth + 40) / width));
    if (String(copies) === marquee.dataset.mlCopies) return;
    marquee.dataset.mlCopies = copies;

    var extra = '';
    for (var i = 1; i < copies; i++) extra += baseHTML;
    var temp = doc.createElement('div');
    temp.innerHTML = extra;
    each(temp.querySelectorAll('[data-shopify-editor-block]'), function (el) {
      el.removeAttribute('data-shopify-editor-block');
    });
    each(temp.children, function (el) {
      el.setAttribute('aria-hidden', 'true');
    });
    each(temp.querySelectorAll('a'), function (el) {
      el.setAttribute('tabindex', '-1');
    });

    group.innerHTML = baseHTML + temp.innerHTML;
    mirror.innerHTML = group.innerHTML;
    each(mirror.querySelectorAll('a'), function (el) {
      el.setAttribute('tabindex', '-1');
    });

    // Keep the speed constant regardless of how many copies were needed.
    var speed = parseFloat(marquee.dataset.mlSpeed) || 34;
    marquee.style.setProperty('--ml-marquee-duration', speed * baseRepeats * copies + 's');
  }

  var marqueeResizeBound = false;

  function initMarquee(scope) {
    var marquees = scope.querySelectorAll('[data-ml-marquee]');
    each(marquees, fitMarquee);
    if (marqueeResizeBound || !marquees.length) return;
    marqueeResizeBound = true;

    var timer = 0;
    window.addEventListener(
      'resize',
      function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          each(doc.querySelectorAll('[data-ml-marquee]'), fitMarquee);
        }, 200);
      },
      { passive: true }
    );
  }

  /* ------------------------------------------------------ search shortcut */
  function isTypingTarget(el) {
    if (!el) return false;
    var tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
  }

  function searchPopupOpen() {
    var popup = doc.querySelector('[data-search-popup]');
    return !!popup && !popup.classList.contains('invisible');
  }

  function openSearch() {
    var triggers = doc.querySelectorAll('[data-open-search-popup]');
    for (var i = 0; i < triggers.length; i++) {
      if (triggers[i].offsetParent !== null) {
        ML.lastSearchTrigger = triggers[i].querySelector('button, [tabindex]') || triggers[i];
        triggers[i].click();
        return true;
      }
    }
    return false;
  }

  var keysBound = false;

  function initKeyboard() {
    if (keysBound) return;
    keysBound = true;

    var isMac = /Mac|iPhone|iPad/.test(navigator.platform || '');
    each(doc.querySelectorAll('[data-ml-kbd]'), function (el) {
      el.textContent = isMac ? '⌘K' : 'Ctrl K';
    });

    doc.addEventListener('keydown', function (event) {
      var key = (event.key || '').toLowerCase();

      if (cfg.searchShortcut !== false) {
        var comboK = (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && key === 'k';
        var slash = key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey;
        if ((comboK || slash) && !isTypingTarget(doc.activeElement) && !searchPopupOpen()) {
          if (openSearch()) event.preventDefault();
          return;
        }
      }

      if (key === 'escape' && searchPopupOpen()) {
        var close = doc.querySelector('[data-search-popup] [data-close-search]');
        var popup = doc.querySelector('[data-search-popup]');
        if (close && close.offsetParent !== null) close.click();
        else if (popup) popup.click();
        if (ML.lastSearchTrigger && ML.lastSearchTrigger.focus) ML.lastSearchTrigger.focus();
      }
    });

    // Non-native "button" controls Minimog renders as <a>/<span>: make Enter/Space work.
    doc.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      var el = event.target;
      if (!el || !el.matches || !el.matches('[role="button"][tabindex]:not(button)')) return;
      event.preventDefault();
      el.click();
    });
  }

  /* ------------------------------------------------------------ mobile nav */
  // Minimog only closes its nav drawer on an overlay click. Add Escape, a close button
  // and focus hand-off, all by driving Minimog's own overlay handler.
  var navBound = false;

  function navWrapper() {
    return doc.querySelector('.sf-menu-wrapper-mobile');
  }

  function navIsOpen() {
    var wrapper = navWrapper();
    return !!wrapper && !wrapper.classList.contains('hidden');
  }

  function closeNav() {
    var wrapper = navWrapper();
    if (!wrapper) return;
    wrapper.click();
    var trigger = doc.querySelector('[data-ml-menu-button]');
    if (trigger && trigger.offsetParent !== null) trigger.focus();
  }

  function initNav() {
    if (navBound) return;
    navBound = true;
    doc.addEventListener('click', function (event) {
      if (!event.target.closest) return;
      if (event.target.closest('[data-ml-nav-close]')) {
        closeNav();
        return;
      }
      if (event.target.closest('[data-ml-menu-button]')) {
        window.setTimeout(function () {
          var close = doc.querySelector('[data-ml-nav-close]');
          if (close && navIsOpen()) close.focus();
        }, 320);
      }
    });
    doc.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && navIsOpen()) closeNav();
    });
  }

  /* ------------------------------------------------------------ colour mode */
  // The mode is applied before first paint by snippets/ml-design-tokens.liquid. This only handles
  // the toggle: flip <html data-ml-theme>, remember the choice, keep every toggle's state in sync.
  var themeBound = false;

  function currentMode() {
    return root.getAttribute('data-ml-theme') === 'dark' ? 'dark' : 'light';
  }

  function syncThemeToggles() {
    var dark = currentMode() === 'dark';
    each(doc.querySelectorAll('[data-ml-theme-toggle]'), function (button) {
      button.setAttribute('aria-pressed', dark ? 'true' : 'false');
      button.setAttribute(
        'aria-label',
        dark ? button.dataset.labelLight || 'Switch to light mode' : button.dataset.labelDark || 'Switch to dark mode'
      );
    });
  }

  function applyMode(mode) {
    root.setAttribute('data-ml-theme', mode);
    var meta = doc.querySelector('meta[name="theme-color"]');
    if (meta && ML.themeColors && ML.themeColors[mode]) meta.setAttribute('content', ML.themeColors[mode]);
    syncThemeToggles();
  }

  // Swapping the palette restyles the whole document. Per-element colour transitions made that janky,
  // so they are disabled for the swap and the browser cross-fades a single snapshot instead.
  var modeSwitching = false;

  function setMode(mode) {
    if (modeSwitching) return;
    try {
      window.localStorage.setItem('ml-theme', mode);
    } catch (e) {}

    root.classList.add('ml-theme-switching');
    var release = function () {
      root.classList.remove('ml-theme-switching');
      modeSwitching = false;
    };

    if (typeof doc.startViewTransition === 'function' && !reduceMotionQuery.matches) {
      modeSwitching = true;
      var transition = doc.startViewTransition(function () {
        applyMode(mode);
      });
      transition.finished.then(release, release);
      return;
    }

    applyMode(mode);
    // Two frames: one to commit the new styles with transitions off, one before turning them back on.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(release);
    });
  }

  function initThemeToggle() {
    syncThemeToggles();
    if (themeBound) return;
    themeBound = true;
    doc.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('[data-ml-theme-toggle]');
      if (!button) return;
      setMode(currentMode() === 'dark' ? 'light' : 'dark');
    });
    // Keep tabs in step with each other.
    window.addEventListener('storage', function (event) {
      if (event.key !== 'ml-theme' || (event.newValue !== 'light' && event.newValue !== 'dark')) return;
      root.setAttribute('data-ml-theme', event.newValue);
      syncThemeToggles();
    });
  }

  /* ---------------------------------------------------------- custom select */
  // Upgrades native <select> elements to the theme's own listbox. The real <select> stays in the
  // DOM (hidden visually) and receives the value plus bubbling input/change events, so forms,
  // Minimog's listeners and Shopify's country/province logic keep working untouched.
  // Skipped: multi-selects, [data-ml-native], and anything inside .sf__custom-select, which
  // Minimog already converts to its own (themed) dropdown.
  var selectSeq = 0;
  var openSelect = null;
  var CHEVRON =
    '<svg class="ml-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m6 9 6 6 6-6"/></svg>';

  // Markup cloned from an upgraded select carries the wrapper and button but none of the listeners
  // (and no select.mlSelect). Put the bare <select> back so it can be upgraded properly.
  function unwrapStaleSelect(select) {
    var wrap = select.parentElement;
    if (!wrap || !wrap.classList.contains('ml-select') || select.mlSelect) return;
    select.removeAttribute('tabindex');
    select.removeAttribute('aria-hidden');
    wrap.parentNode.insertBefore(select, wrap);
    wrap.parentNode.removeChild(wrap);
  }

  function selectEligible(select) {
    if (select.multiple || select.size > 1) return false;
    if (select.hasAttribute('data-ml-native') || select.hidden) return false;
    if (select.closest('.ml-select, .sf__custom-select, noscript, template')) return false;
    // A select the theme hides on purpose (e.g. the sticky bar's variant list on single-variant
    // products uses a `hidden` class) must stay hidden: wrapping it would put a control on screen.
    if (window.getComputedStyle(select).display === 'none') return false;
    return true;
  }

  function closeSelect(api, returnFocus) {
    if (!api || !api.wrap.classList.contains('is-open')) return;
    api.wrap.classList.remove('is-open', 'ml-select--up', 'ml-select--right');
    api.button.setAttribute('aria-expanded', 'false');
    api.button.removeAttribute('aria-activedescendant');
    api.list.hidden = true;
    if (openSelect === api) openSelect = null;
    if (returnFocus) api.button.focus();
  }

  function buildSelect(select) {
    var id = 'ml-select-' + ++selectSeq;
    var wrap = doc.createElement('span');
    wrap.className = 'ml-select';
    var computed = window.getComputedStyle(select);
    var parentWidth = select.parentElement ? select.parentElement.clientWidth : 0;
    if (
      computed.display === 'block' ||
      select.classList.contains('w-full') ||
      (parentWidth && select.offsetWidth >= parentWidth * 0.9)
    ) {
      wrap.classList.add('ml-select--block');
    }

    var button = doc.createElement('button');
    button.type = 'button';
    button.className = 'ml-select__button';
    button.setAttribute('role', 'combobox');
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', id);
    var valueEl = doc.createElement('span');
    valueEl.className = 'ml-select__value';
    button.appendChild(valueEl);
    button.insertAdjacentHTML('beforeend', CHEVRON);

    var list = doc.createElement('ul');
    list.className = 'ml-select__list';
    list.id = id;
    list.hidden = true;
    list.setAttribute('role', 'listbox');
    list.setAttribute('tabindex', '-1');

    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);
    wrap.appendChild(button);
    wrap.appendChild(list);
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    // Accessible name: reuse the select's label.
    var label = select.getAttribute('aria-label');
    if (!label && select.id) {
      var safeId = window.CSS && CSS.escape ? CSS.escape(select.id) : select.id;
      var labelEl = doc.querySelector('label[for="' + safeId + '"]');
      if (labelEl) label = labelEl.textContent.trim();
    }
    if (label) {
      button.setAttribute('aria-label', label);
      list.setAttribute('aria-label', label);
    }

    var api = { select: select, wrap: wrap, button: button, list: list, items: [], active: -1, typed: '', typedAt: 0 };

    function sync() {
      var index = select.selectedIndex;
      var option = select.options[index];
      valueEl.textContent = option ? option.textContent.trim() : '';
      each(api.items, function (item, i) {
        item.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      // Some selects are filled in by script after render (e.g. the cart's country list). Until
      // options exist there is nothing to open, so present that as a loading state, not a dead control.
      var empty = api.items.length === 0;
      button.disabled = select.disabled || empty;
      wrap.classList.toggle('is-empty', empty);
      if (empty) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }

    function render() {
      list.innerHTML = '';
      api.items = [];
      var lastGroup = null;
      each(select.options, function (option, index) {
        var group = option.parentNode && option.parentNode.tagName === 'OPTGROUP' ? option.parentNode : null;
        if (group && group !== lastGroup) {
          var groupEl = doc.createElement('li');
          groupEl.className = 'ml-select__group';
          groupEl.setAttribute('role', 'presentation');
          groupEl.textContent = group.label;
          list.appendChild(groupEl);
        }
        lastGroup = group;
        var item = doc.createElement('li');
        item.className = 'ml-select__option';
        item.id = id + '-opt-' + index;
        item.setAttribute('role', 'option');
        item.dataset.index = index;
        item.textContent = option.textContent.trim();
        if (option.disabled) item.setAttribute('aria-disabled', 'true');
        list.appendChild(item);
        api.items.push(item);
      });
      sync();
    }

    function setActive(index, scroll) {
      if (!api.items.length) return;
      index = Math.max(0, Math.min(api.items.length - 1, index));
      each(api.items, function (item, i) {
        item.classList.toggle('is-active', i === index);
      });
      api.active = index;
      button.setAttribute('aria-activedescendant', api.items[index].id);
      if (scroll) api.items[index].scrollIntoView({ block: 'nearest' });
    }

    function step(from, direction) {
      var i = from;
      do {
        i += direction;
      } while (api.items[i] && api.items[i].getAttribute('aria-disabled') === 'true');
      return api.items[i] ? i : from;
    }

    function open() {
      if (select.disabled || !api.items.length) return;
      if (openSelect && openSelect !== api) closeSelect(openSelect, false);
      list.hidden = false;
      // Flip up / align right when the list would leave its scroll container or the viewport.
      var bounds = { top: 0, bottom: window.innerHeight, right: window.innerWidth };
      var scroller = wrap.parentElement;
      while (scroller && scroller !== doc.body) {
        if (/(auto|scroll|hidden)/.test(window.getComputedStyle(scroller).overflowY)) {
          var r = scroller.getBoundingClientRect();
          bounds.top = Math.max(bounds.top, r.top);
          bounds.bottom = Math.min(bounds.bottom, r.bottom);
          bounds.right = Math.min(bounds.right, r.right);
          break;
        }
        scroller = scroller.parentElement;
      }
      var box = button.getBoundingClientRect();
      var height = Math.min(list.scrollHeight, 320);
      var below = bounds.bottom - box.bottom;
      var above = box.top - bounds.top;
      wrap.classList.toggle('ml-select--up', below < height + 12 && above > below);
      wrap.classList.toggle('ml-select--right', box.left + list.offsetWidth > bounds.right - 8);
      void list.offsetHeight;
      wrap.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      openSelect = api;
      setActive(select.selectedIndex >= 0 ? select.selectedIndex : 0, true);
    }

    function choose(index) {
      var item = api.items[index];
      if (!item || item.getAttribute('aria-disabled') === 'true') return;
      var changed = select.selectedIndex !== index;
      select.selectedIndex = index;
      sync();
      closeSelect(api, true);
      if (changed) {
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    api.sync = sync;
    api.render = render;

    button.addEventListener('click', function () {
      if (wrap.classList.contains('is-open')) closeSelect(api, false);
      else open();
    });

    list.addEventListener('click', function (event) {
      var item = event.target.closest('.ml-select__option');
      if (item) choose(parseInt(item.dataset.index, 10));
    });

    list.addEventListener('pointermove', function (event) {
      var item = event.target.closest('.ml-select__option');
      if (item) setActive(parseInt(item.dataset.index, 10), false);
    });

    button.addEventListener('keydown', function (event) {
      var isOpen = wrap.classList.contains('is-open');
      var key = event.key;

      if (!isOpen) {
        if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
          event.preventDefault();
          open();
        }
        return;
      }

      // While open, keys belong to the listbox: Escape must not also close a drawer behind it.
      event.stopPropagation();
      // A space typed mid-search ("united states") is part of the search, not a selection.
      var searching = api.typed !== '' && Date.now() - api.typedAt <= 700;
      if (key === ' ' && searching) {
        event.preventDefault();
        api.typed += ' ';
        api.typedAt = Date.now();
        return;
      }
      if (key === 'Escape') {
        event.preventDefault();
        closeSelect(api, true);
      } else if (key === 'ArrowDown') {
        event.preventDefault();
        setActive(step(api.active, 1), true);
      } else if (key === 'ArrowUp') {
        event.preventDefault();
        setActive(step(api.active, -1), true);
      } else if (key === 'Home') {
        event.preventDefault();
        setActive(step(-1, 1), true);
      } else if (key === 'End') {
        event.preventDefault();
        setActive(step(api.items.length, -1), true);
      } else if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        choose(api.active);
      } else if (key === 'Tab') {
        closeSelect(api, false);
      } else if (key.length === 1) {
        var now = Date.now();
        api.typed = now - api.typedAt > 700 ? key : api.typed + key;
        api.typedAt = now;
        var needle = api.typed.toLowerCase();
        for (var i = 0; i < api.items.length; i++) {
          if (api.items[i].textContent.toLowerCase().indexOf(needle) === 0) {
            setActive(i, true);
            break;
          }
        }
      }
    });

    // Value set by the real select (scripts) and options replaced (country -> provinces).
    select.addEventListener('change', sync);
    if ('MutationObserver' in window) {
      new MutationObserver(render).observe(select, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled']
      });
    }

    select.mlSelect = api;
    render();
  }

  var selectsBound = false;

  function initSelects(scope) {
    each((scope || doc).querySelectorAll('select'), function (select) {
      if (select.mlSelect) return;
      unwrapStaleSelect(select);
      if (!selectEligible(select)) return;
      try {
        buildSelect(select);
      } catch (error) {
        /* leave the native control in place */
      }
    });

    if (selectsBound) return;
    selectsBound = true;

    doc.addEventListener('pointerdown', function (event) {
      if (openSelect && !openSelect.wrap.contains(event.target)) closeSelect(openSelect, false);
    });
    // Only a width change invalidates the list position. Mobile browsers also fire resize when the
    // URL bar collapses, which must not close a list someone is scrolling.
    var lastWidth = window.innerWidth;
    window.addEventListener(
      'resize',
      function () {
        if (window.innerWidth === lastWidth) return;
        lastWidth = window.innerWidth;
        if (openSelect) closeSelect(openSelect, false);
      },
      { passive: true }
    );
    // Form reset restores the select's value without firing change.
    doc.addEventListener('reset', function (event) {
      window.setTimeout(function () {
        each(event.target.querySelectorAll('select'), function (select) {
          if (select.mlSelect) select.mlSelect.sync();
        });
      }, 0);
    });

    // Drawers, quick view and the cart are injected outside #MainContent, so watch the body.
    if ('MutationObserver' in window) {
      var frame = 0;
      new MutationObserver(function (mutations) {
        var relevant = false;
        for (var i = 0; i < mutations.length && !relevant; i++) {
          var nodes = mutations[i].addedNodes;
          for (var j = 0; j < nodes.length; j++) {
            var node = nodes[j];
            if (node.nodeType !== 1 || node.closest('.ml-select')) continue;
            if (node.tagName === 'SELECT' || node.querySelector('select')) {
              relevant = true;
              break;
            }
          }
        }
        if (!relevant || frame) return;
        frame = window.requestAnimationFrame(function () {
          frame = 0;
          initSelects(doc);
        });
      }).observe(doc.body, { childList: true, subtree: true });
    }
  }

  /* -------------------------------------------------------------- skeletons */
  // Placeholders for content that is fetched after render. Each one is removed by the event that
  // means "the real thing is here" (a resolved fetch, a class Minimog flips, an observed mutation).
  // There are deliberately no timers in this block.

  var SKELETON_CARD =
    '<div class="ml-card ml-card--skeleton" aria-hidden="true">' +
    '<div class="ml-skel ml-skel--media"></div>' +
    '<div class="ml-skel ml-skel--line ml-skel--xs"></div>' +
    '<div class="ml-skel ml-skel--line"></div>' +
    '<div class="ml-skel ml-skel--line ml-skel--md"></div>' +
    '<div class="ml-skel ml-skel--line ml-skel--sm"></div>' +
    '<div class="ml-skel ml-skel--button"></div>' +
    '</div>';

  function skeletonCards(count) {
    var html = '';
    for (var i = 0; i < count; i++) html += SKELETON_CARD;
    return html;
  }

  /* Recommendations ---------------------------------------------------------
     <ml-recommendations> is server-rendered with skeleton cards inside. It fetches the section
     itself, so it knows exactly when the data arrived, came back empty, or failed. */
  function defineRecommendations() {
    if (!('customElements' in window)) {
      // No upgrade will ever happen: do not leave placeholders on screen.
      each(doc.querySelectorAll('ml-recommendations'), function (el) {
        el.innerHTML = '';
      });
      return;
    }
    if (window.customElements.get('ml-recommendations')) return;

    window.customElements.define(
      'ml-recommendations',
      class extends HTMLElement {
        connectedCallback() {
          if (this.mlArmed) return;
          this.mlArmed = true;
          var self = this;
          if (!('IntersectionObserver' in window)) {
            self.load();
            return;
          }
          this.mlObserver = new IntersectionObserver(
            function (entries, observer) {
              if (!entries[0].isIntersecting) return;
              observer.disconnect();
              self.load();
            },
            { rootMargin: '0px 0px 400px 0px' }
          );
          this.mlObserver.observe(this);
        }

        disconnectedCallback() {
          if (this.mlObserver) this.mlObserver.disconnect();
        }

        load() {
          var self = this;
          var url = this.dataset.url;
          if (!url) return this.settle(null);
          this.setAttribute('aria-busy', 'true');
          window
            .fetch(url)
            .then(function (response) {
              if (!response.ok) throw new Error('recommendations ' + response.status);
              return response.text();
            })
            .then(function (html) {
              var parsed = new DOMParser().parseFromString(html, 'text/html');
              var source = parsed.querySelector('ml-recommendations');
              var hasCards = source && source.querySelector('.ml-card:not(.ml-card--skeleton)');
              self.settle(hasCards ? source.innerHTML : null);
            })
            .catch(function () {
              self.settle(null);
            });
        }

        // html === null: nothing to recommend (or the request failed) -> the section collapses.
        settle(html) {
          this.removeAttribute('aria-busy');
          this.innerHTML = html || '';
          if (!html) return;
          init(this);
          var theme = window.MinimogTheme || {};
          if (theme.Wishlist && theme.Wishlist.setWishlistButtonsState) theme.Wishlist.setWishlistButtonsState();
          if (theme.CompareProduct && theme.CompareProduct.setCompareButtonsState) theme.CompareProduct.setCompareButtonsState();
        }
      }
    );
  }

  /* Recently viewed ---------------------------------------------------------
     Minimog keeps this section `hidden` until every card has been fetched, then removes the class.
     While it is hidden and its cookie says there is something to load, show a placeholder section
     and remove it the moment that class changes. */
  function recentlyViewedHandles(section) {
    try {
      var match = decodeURIComponent(doc.cookie).match(/(?:^|;\s*)sf-recent-viewed-products=([^;]*)/);
      if (!match) return [];
      var handles = JSON.parse(match[1]) || [];
      var current = section.dataset.productHandle;
      var seen = {};
      return handles.filter(function (handle) {
        if (!handle || handle === current || seen[handle]) return false;
        seen[handle] = true;
        return true;
      });
    } catch (e) {
      return [];
    }
  }

  function initRecentlyViewedSkeleton(scope) {
    each((scope || doc).querySelectorAll('[data-section-type="recently-viewed"]'), function (section) {
      once(section, 'RecentSkeleton', function () {
        if (!section.classList.contains('hidden') || !('MutationObserver' in window)) return;
        var handles = recentlyViewedHandles(section);
        if (!handles.length) return;

        var limit = parseInt(section.dataset.productsToShow, 10) || 8;
        var perRow = parseInt(section.dataset.productsPerRow, 10) || 4;
        var count = Math.min(handles.length, limit, perRow);
        var heading = section.querySelector('.section__heading');

        var placeholder = doc.createElement('div');
        placeholder.className = 'ml-skel-section';
        placeholder.setAttribute('aria-busy', 'true');
        placeholder.innerHTML =
          '<div class="container">' +
          (heading ? '<h2 class="section__heading">' + heading.innerHTML + '</h2>' : '<div class="ml-skel ml-skel-title"></div>') +
          '<div class="ml-grid ml-product-grid" style="--ml-cols-d:' + perRow + ';--ml-cols-m:2">' +
          skeletonCards(count) +
          '</div></div>';
        section.parentNode.insertBefore(placeholder, section);

        var expected = handles.length;
        var finished = 0;
        var classObserver = null;
        var perfObserver = null;

        function done() {
          if (classObserver) classObserver.disconnect();
          if (perfObserver) perfObserver.disconnect();
          window.removeEventListener('offline', done);
          if (placeholder.parentNode) placeholder.parentNode.removeChild(placeholder);
        }

        // Success path: Minimog reveals the real section.
        classObserver = new MutationObserver(function () {
          if (!section.classList.contains('hidden') || !section.isConnected) done();
        });
        classObserver.observe(section, { attributes: true, attributeFilter: ['class'] });

        // Failure path: if one of Minimog's card requests rejects it never reveals the section.
        // Once every card request has finished and the section is still hidden, stop waiting.
        if ('PerformanceObserver' in window) {
          try {
            perfObserver = new PerformanceObserver(function (list) {
              list.getEntries().forEach(function (entry) {
                if (entry.name.indexOf('view=grid-card-item') !== -1) finished++;
              });
              if (finished < expected) return;
              window.requestAnimationFrame(function () {
                window.requestAnimationFrame(function () {
                  if (section.classList.contains('hidden')) done();
                });
              });
            });
            perfObserver.observe({ type: 'resource', buffered: true });
          } catch (e) {}
        }
        window.addEventListener('offline', done);
      });
    });
  }

  /* Predictive search -------------------------------------------------------
     Minimog shows [data-spinner] (inline visibility) for exactly as long as a query is in flight.
     Mirror that onto the component and show result-shaped placeholders in the meantime. */
  function initSearchSkeleton() {
    each(doc.querySelectorAll('predictive-search'), function (component) {
      once(component, 'SearchSkeleton', function () {
        var spinner = component.querySelector('[data-spinner]');
        var results = component.querySelector('[data-predictive-search]');
        if (!spinner || !results || !('MutationObserver' in window)) return;
        var grid = null;

        function update() {
          var loading = spinner.style.visibility === 'visible';
          if (loading && !grid) {
            grid = doc.createElement('div');
            grid.className = 'ml-skel-grid';
            grid.setAttribute('aria-hidden', 'true');
            var tiles = '';
            for (var i = 0; i < 5; i++) {
              tiles +=
                '<div class="ml-card--skeleton"><div class="ml-skel ml-skel--media"></div>' +
                '<div class="ml-skel ml-skel--line"></div><div class="ml-skel ml-skel--line ml-skel--sm"></div></div>';
            }
            grid.innerHTML = tiles;
            results.appendChild(grid);
            results.classList.remove('hidden');
            component.setAttribute('data-ml-loading', '');
            results.setAttribute('aria-busy', 'true');
          } else if (!loading && grid) {
            if (grid.parentNode) grid.parentNode.removeChild(grid);
            grid = null;
            component.removeAttribute('data-ml-loading');
            results.removeAttribute('aria-busy');
          }
        }

        new MutationObserver(update).observe(spinner, { attributes: true, attributeFilter: ['style'] });
        // Minimog replaces the results' children when data arrives; make sure ours never lingers.
        new MutationObserver(function () {
          if (grid && !grid.parentNode) {
            grid = null;
            component.removeAttribute('data-ml-loading');
            results.removeAttribute('aria-busy');
          }
        }).observe(results, { childList: true });
      });
    });
  }

  /* ---------------------------------------------------------------- compare */
  // Minimog keeps the compared products in localStorage but shows no count anywhere. This mirrors
  // that list onto [data-ml-compare-count] badges (header icon, "View comparison" link).
  var compareBound = false;

  function readCompareCount() {
    try {
      var list = JSON.parse(window.localStorage.getItem('sf__compare-products') || '[]');
      return Array.isArray(list) ? list.length : 0;
    } catch (e) {
      return 0;
    }
  }

  function syncCompareCount() {
    var count = readCompareCount();
    each(doc.querySelectorAll('[data-ml-compare-count]'), function (badge) {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
    each(doc.querySelectorAll('.ml-compare-view'), function (link) {
      link.classList.toggle('is-active', count > 0);
    });
  }

  function initCompareCount() {
    syncCompareCount();
    if (compareBound) return;
    compareBound = true;
    // Minimog updates storage inside its own click handler; read it on the next frame.
    doc.addEventListener('click', function (event) {
      if (!event.target.closest || !event.target.closest('.sf-prod-compare__button, .sf-prod-compare__remove')) return;
      window.requestAnimationFrame(syncCompareCount);
    });
    window.addEventListener('storage', function (event) {
      if (event.key === 'sf__compare-products') syncCompareCount();
    });
  }

  /* ----------------------------------------------------------- cart drawer */
  // Minimog's drawer closes on its button/overlay; add Escape and focus return.
  var cartBound = false;

  function initCartDrawer() {
    if (cartBound) return;
    cartBound = true;
    doc.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var wrapper = doc.querySelector('.scd__wrapper');
      if (!wrapper || wrapper.classList.contains('hidden')) return;
      var close = wrapper.querySelector('.scd__close');
      if (!close) return;
      close.click();
      var triggers = doc.querySelectorAll('.sf-cart-icon');
      for (var i = 0; i < triggers.length; i++) {
        if (triggers[i].offsetParent !== null) {
          triggers[i].focus();
          break;
        }
      }
    });
  }

  /* -------------------------------------------------------- drawer extras */
  // Bag count in the cart drawer title, the search entry in the mobile menu, and the current-page
  // marker on menu links. Cart data comes from Minimog's own event bus; nothing here touches the cart.
  var drawerExtrasBound = false;

  function setBagCount(count) {
    each(doc.querySelectorAll('[data-ml-cart-count]'), function (el) {
      el.textContent = count;
      el.hidden = !(count > 0);
    });
  }

  function markCurrentNavLinks() {
    var here = window.location.pathname.replace(/\/$/, '') || '/';
    each(doc.querySelectorAll('.sf-menu-wrapper-mobile .sf-menu-links a[href]'), function (link) {
      var path;
      try { path = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '') || '/'; } catch (e) { return; }
      if (path === here) link.setAttribute('aria-current', 'page');
    });
  }

  function initDrawerExtras() {
    markCurrentNavLinks();
    if (drawerExtrasBound) return;
    drawerExtrasBound = true;

    var bus = window.MinimogEvents;
    if (bus && typeof bus.subscribe === 'function') {
      bus.subscribe('ON_CART_UPDATE', function (cart) {
        if (cart && typeof cart.item_count !== 'undefined') setBagCount(Number(cart.item_count));
      });
    }

    // Minimog also writes the count into every .sf-cart-count; mirroring that node covers the case
    // where this file boots before the event bus exists.
    var badge = doc.querySelector('.sf-cart-count');
    if (badge && 'MutationObserver' in window) {
      new MutationObserver(function () {
        var n = parseInt(badge.textContent, 10);
        if (!isNaN(n)) setBagCount(n);
      }).observe(badge, { childList: true, characterData: true, subtree: true });
    }

    doc.addEventListener('click', function (event) {
      var trigger = event.target.closest ? event.target.closest('[data-ml-nav-search]') : null;
      if (!trigger) return;
      event.preventDefault();
      var close = doc.querySelector('[data-ml-nav-close]');
      if (close) close.click();
      var opener = doc.querySelector('.sf-header__mobile [data-open-search-popup], [data-screen="sf-header__mobile"] [data-open-search-popup]') || doc.querySelector('[data-open-search-popup]');
      if (opener) (opener.querySelector('[role="button"]') || opener).click();
    });
  }

  /* --------------------------------------------------------- filter drawer */
  // Minimog applies facets instantly; "View results" simply closes its drawer.
  var filterBound = false;

  function initFilterActions() {
    if (filterBound) return;
    filterBound = true;
    doc.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('[data-ml-filter-apply]');
      if (!button) return;
      var sidebar = button.closest('.sf__sidebar');
      var close = sidebar && sidebar.querySelector('.sf__sidebar-close');
      if (close) close.click();
    });
  }

  /* -------------------------------------------------------- image fallback */
  var fallbackBound = false;

  function initFallbackImages() {
    if (fallbackBound) return;
    fallbackBound = true;
    doc.addEventListener(
      'error',
      function (event) {
        var el = event.target;
        if (el && el.tagName === 'IMG' && el.hasAttribute('data-ml-fallback')) el.classList.add('is-broken');
      },
      true
    );
  }

  /* -------------------------------------------------------- colour swatches */
  // Minimog only paints a swatch when the option value IS a palette name ("green").
  // Real catalogue values are messier ("2 x Green", "Matte Black", "Gray"), and an
  // unknown value leaves a blank white dot. This resolves the colour word inside the
  // value, then falls back to the variant's own image, then to initials.
  var SWATCH_SELECTOR = '.product-option-item[data-option-type="color"]';
  var SWATCH_EXTRA = {
    gray: '#E0E0E0',
    gold: '#D9B65B',
    navy: '#1F2A44',
    khaki: '#C8B88A',
    transparent: 'linear-gradient(135deg, #fff 0 45%, #dfe5e8 45% 55%, #fff 55%)',
    clear: 'linear-gradient(135deg, #fff 0 45%, #dfe5e8 45% 55%, #fff 55%)',
    multicolor: 'conic-gradient(#FF6961, #FDDA76, #C1E1C1, #8DB4D2, #B19CD9, #FF6961)',
    multicolour: 'conic-gradient(#FF6961, #FDDA76, #C1E1C1, #8DB4D2, #B19CD9, #FF6961)',
    multi: 'conic-gradient(#FF6961, #FDDA76, #C1E1C1, #8DB4D2, #B19CD9, #FF6961)',
    rainbow: 'conic-gradient(#FF6961, #FDDA76, #C1E1C1, #8DB4D2, #B19CD9, #FF6961)'
  };
  var swatchPalette = null;

  function getSwatchPalette() {
    if (swatchPalette) return swatchPalette;
    var map = {};
    Object.keys(SWATCH_EXTRA).forEach(function (key) {
      map[key] = SWATCH_EXTRA[key];
    });
    var raw = (window.MinimogSettings && window.MinimogSettings.product_colors) || '';
    if (!raw) return map; // Minimog settings not parsed yet: do not cache
    raw.split(',').forEach(function (pair) {
      var parts = pair.split(':');
      var key = (parts[0] || '').trim().toLowerCase();
      var value = (parts[1] || '').trim();
      if (key && value) map[key] = value;
    });
    swatchPalette = map;
    return map;
  }

  function resolveSwatch(value) {
    var palette = getSwatchPalette();
    var clean = value.toLowerCase().replace(/[^a-z\s-]/g, ' ').replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
    if (!clean) return '';
    if (palette[clean]) return palette[clean];
    var words = clean.split(' ');
    // longest run of words first, so "rose gold" wins over "gold"
    for (var size = words.length; size > 0; size--) {
      for (var start = 0; start + size <= words.length; start++) {
        var phrase = words.slice(start, start + size).join(' ');
        if (palette[phrase]) return palette[phrase];
      }
    }
    for (var i = 0; i < words.length; i++) {
      if (words[i].length > 2 && window.CSS && CSS.supports('color', words[i])) return words[i];
    }
    return '';
  }

  function swatchVariantImage(el, value) {
    var picker = el.closest('[data-pcard-variant-picker]');
    var script = picker && picker.querySelector('script[type="application/json"]');
    if (!script) return '';
    try {
      var position = 'option' + (el.dataset.optionPosition || '1');
      var variants = JSON.parse(script.textContent);
      for (var i = 0; i < variants.length; i++) {
        var image = variants[i].featured_image;
        if (variants[i][position] === value && image && image.src) {
          return image.src + (image.src.indexOf('?') > -1 ? '&' : '?') + 'width=96';
        }
      }
    } catch (e) {}
    return '';
  }

  function initSwatches(scope) {
    each((scope || doc).querySelectorAll(SWATCH_SELECTOR), function (el) {
      if (el.hasAttribute('data-ml-swatch')) return;
      var holder = el.hasAttribute('data-value') ? el : el.closest('[data-value]');
      var value = holder && holder.getAttribute('data-value');
      if (!value) return;
      if (!window.MinimogSettings || !window.MinimogSettings.product_colors) return; // retry on the next pass
      el.setAttribute('data-ml-swatch', '');
      // exact palette names are painted here too: Minimog's own pass runs seconds after load
      var paint = resolveSwatch(value);
      if (paint) {
        el.style.background = paint;
        return;
      }
      var image = swatchVariantImage(el, value);
      if (image) {
        el.style.background = 'url("' + image + '") center / cover no-repeat';
        return;
      }
      var initials = value.replace(/[^A-Za-z0-9 ]/g, '').split(' ').filter(Boolean).slice(0, 2).map(function (word) { return word.charAt(0); }).join('');
      el.setAttribute('data-ml-swatch', initials.toUpperCase());
    });
  }

  var swatchObserver = null;

  function initSwatchObserver() {
    if (swatchObserver || !('MutationObserver' in window)) return;
    var frame = 0;
    // body, not #MainContent: quick view and the cart drawer render outside it
    swatchObserver = new MutationObserver(function () {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = 0;
        initSwatches(doc);
      });
    });
    swatchObserver.observe(doc.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------ gallery autoplay */
  // The product gallery is Minimog's Swiper. Its autoplay module ships disabled, so this only switches
  // it on once the slider exists (the class Swiper adds when it finishes initialising - no timer).
  // Hover pauses it; choosing an image, swiping, zooming or changing variant ends it for the visit,
  // because a gallery that moves away from the picture someone chose is worse than a still one.
  function startGalleryAutoplay(gallery, container) {
    var swiper = container.swiper;
    if (!swiper || !swiper.autoplay || swiper.slides.length < 2) return;
    if (gallery.offsetParent === null) return; // the hidden desktop/mobile twin
    var delay = (parseInt(gallery.getAttribute('data-ml-autoplay-delay'), 10) || 4) * 1000;
    swiper.params.autoplay = Object.assign({}, swiper.params.autoplay, {
      enabled: true,
      delay: delay,
      disableOnInteraction: true,
      pauseOnMouseEnter: false,
      stopOnLastSlide: false
    });
    swiper.autoplay.start();

    var stopped = false;
    function stop() {
      if (stopped) return;
      stopped = true;
      swiper.autoplay.stop();
    }
    gallery.addEventListener('mouseenter', function () { if (!stopped) swiper.autoplay.stop(); });
    gallery.addEventListener('mouseleave', function () { if (!stopped) swiper.autoplay.start(); });
    gallery.addEventListener('focusin', stop);
    gallery.addEventListener('pointerdown', stop);
    swiper.on('touchStart', stop);
    // video and 3D slides should not be pulled away mid-play
    swiper.on('slideChange', function () {
      var slide = swiper.slides[swiper.activeIndex];
      var type = slide && slide.getAttribute('data-media-type');
      if (type && type !== 'image') stop();
    });
    var form = doc.querySelector('.product-template, [data-section-type="product-page"]');
    if (form) form.addEventListener('change', function (event) {
      if (event.target && event.target.closest && event.target.closest('variant-picker, .variant-picker, [data-variant-picker], .product-options, .prod__option')) stop();
    });
  }

  function initGalleryAutoplay(scope) {
    if (reduceMotionQuery.matches || cfg.designMode) return;
    each((scope || doc).querySelectorAll('media-gallery[data-ml-autoplay="true"]'), function (gallery) {
      once(gallery, 'Autoplay', function () {
        var container = gallery.querySelector('.sf-preview__wrapper .swiper-container');
        if (!container) return;
        if (container.swiper) return startGalleryAutoplay(gallery, container);
        if (!('MutationObserver' in window)) return;
        var observer = new MutationObserver(function () {
          if (!container.swiper) return;
          observer.disconnect();
          startGalleryAutoplay(gallery, container);
        });
        observer.observe(container, { attributes: true, attributeFilter: ['class'] });
      });
    });
  }

  /* --------------------------------------------------- settled card images */
  // The head script clears [data-image-loading] from the image's load/error event. This sweep only
  // catches an image that was already complete when its markup arrived (cloned or cached nodes), so
  // a finished card can never be left as a skeleton. It reads real image state; there is no timer.
  function settleImages(scope) {
    each((scope || doc).querySelectorAll('[data-image-loading] img'), function (img) {
      if (!img.complete || !img.currentSrc) return;
      var holder = img.closest('[data-image-loading]');
      if (holder) holder.removeAttribute('data-image-loading');
      img.classList.add('f-img-loaded');
    });
  }

  /* ------------------------------------------------------ dynamic content */
  // A single observer re-scans for reveal targets when Minimog injects markup
  // (recommendations, filtered collection grids, load more, recently viewed).
  var contentObserver = null;

  function initContentObserver() {
    var main = doc.getElementById('MainContent');
    if (contentObserver || !main || !('MutationObserver' in window)) return;
    var frame = 0;
    contentObserver = new MutationObserver(function () {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = 0;
        initReveal(main);
        initAccordions(main);
        settleImages(main);
      });
    });
    contentObserver.observe(main, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ boot */
  function init(scope) {
    scope = scope || doc;
    initSpot(scope);
    initHero(scope);
    initReveal(scope);
    initAccordions(scope);
    initMarquee(scope);
  }

  function boot() {
    initSpotlight();
    initRipple();
    initStepperBump();
    initKeyboard();
    initFallbackImages();
    initFilterActions();
    initNav();
    initCartDrawer();
    initDrawerExtras();
    initCompareCount();
    initThemeToggle();
    initSelects(doc);
    defineRecommendations();
    initRecentlyViewedSkeleton(doc);
    initSearchSkeleton();
    initSwatches(doc);
    initSwatchObserver();
    settleImages(doc);
    initGalleryAutoplay(doc);
    init(doc);
    initContentObserver();
    ML.ready = true;
    root.classList.add('ml-ready');
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();

  reduceMotionQuery.addEventListener &&
    reduceMotionQuery.addEventListener('change', function () {
      if (reduceMotionQuery.matches) {
        root.classList.remove('ml-motion');
        each(doc.querySelectorAll('[data-ml-reveal]'), function (el) {
          el.classList.add('is-in');
        });
      }
    });

  /* ---------------------------------------------------------- Theme Editor */
  doc.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    initSelects(event.target);
    initSwatches(event.target);
    initRecentlyViewedSkeleton(event.target);
    initSearchSkeleton();
    syncThemeToggles();
  });

  doc.addEventListener('shopify:section:unload', function (event) {
    if (!revealObserver) return;
    each(event.target.querySelectorAll('[data-ml-reveal]'), function (el) {
      revealObserver.unobserve(el);
    });
  });

  doc.addEventListener('shopify:section:select', function (event) {
    each(event.target.querySelectorAll('[data-ml-reveal], [data-ml-hero-item]'), function (el) {
      el.classList.add('is-in');
    });
  });

  doc.addEventListener('shopify:section:deselect', function () {});

  doc.addEventListener('shopify:block:select', function (event) {
    var block = event.target;
    block.classList.add('is-in');

    var item = block.closest('.ml-accordion__item');
    if (item) setAccordionItem(item, true, false);

    var marquee = block.closest('[data-ml-marquee]');
    if (marquee) marquee.classList.add('is-paused');

    if (block.scrollIntoView && block.closest('.ml-trust--rail, .ml-product-grid--carousel')) {
      block.scrollIntoView({ block: 'nearest', inline: 'start' });
    }
  });

  doc.addEventListener('shopify:block:deselect', function (event) {
    var marquee = event.target.closest('[data-ml-marquee]');
    if (marquee) marquee.classList.remove('is-paused');
  });

  ML.init = init;
})();
